import { Star } from "lucide-react";
import { useState } from "react";
import { useInput, type InputProps } from "ra-core";
import { cn } from "@/lib/utils";

/**
 * StarRating — reusable display + input para rating 1..5.
 *
 * Modos:
 *   - <StarRating value={3} readOnly />          → muestra solo, sin interacción
 *   - <StarRatingInput source="rating" />        → form input para react-admin
 *
 * Las estrellas pintadas usan currentColor (para que en variante featured
 * tomen el negro sobre amarillo, y en card normal usen --musco-accent).
 */

interface StarRatingProps {
  value?: number | null;
  onChange?: (value: number | null) => void;
  size?: number;
  readOnly?: boolean;
  showEmptyLabel?: boolean;
  className?: string;
  /** Color del relleno (default: var(--musco-accent)). En featured cards usa "#0a0a0a". */
  color?: string;
}

export function StarRating({
  value,
  onChange,
  size = 18,
  readOnly = false,
  showEmptyLabel = false,
  className,
  color,
}: StarRatingProps) {
  const [hover, setHover] = useState<number | null>(null);
  const displayed = hover ?? value ?? 0;
  const interactive = !readOnly && !!onChange;
  const fillColor = color ?? "var(--musco-accent)";

  return (
    <div
      className={cn("inline-flex items-center gap-0.5", className)}
      role={interactive ? "radiogroup" : undefined}
      aria-label="Calificación de la junta"
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const active = n <= displayed;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            onClick={() => {
              if (!interactive) return;
              // Click en la estrella ya seleccionada → toggle off (null)
              onChange?.(value === n ? null : n);
            }}
            onMouseEnter={() => interactive && setHover(n)}
            onMouseLeave={() => interactive && setHover(null)}
            aria-label={`${n} estrella${n !== 1 ? "s" : ""}`}
            aria-pressed={active}
            className={cn(
              "p-0 leading-none transition-transform",
              interactive && "cursor-pointer hover:scale-110",
              readOnly && "cursor-default",
            )}
            style={{ background: "transparent", border: 0, color: fillColor }}
          >
            <Star
              size={size}
              fill={active ? "currentColor" : "transparent"}
              stroke="currentColor"
              strokeWidth={1.6}
              style={{ opacity: active ? 1 : 0.45 }}
            />
          </button>
        );
      })}
      {showEmptyLabel && (value == null || value === 0) ? (
        <span
          className="text-xs ml-1"
          style={{ color: "var(--musco-muted)" }}
        >
          Sin calificar
        </span>
      ) : null}
    </div>
  );
}

/**
 * StarRatingInput — wrapper para react-admin que conecta con react-hook-form
 * vía useInput de ra-core. Compatible con <Form> y `defaultValues`.
 */
export function StarRatingInput({
  source,
  label,
  helperText,
  ...rest
}: InputProps & { helperText?: string }) {
  const { field } = useInput({ source, ...rest });
  return (
    <div className="space-y-1.5">
      {label !== false ? (
        <label className="block text-sm font-medium">
          {label ?? "Calificación"}
        </label>
      ) : null}
      <StarRating
        value={field.value as number | null | undefined}
        onChange={(v) => field.onChange(v)}
        size={28}
        showEmptyLabel
      />
      {helperText ? (
        <p className="text-xs" style={{ color: "var(--musco-muted)" }}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
